// ===========================================================================
// Room tones for the chat client.
//
// A group room is a video grid you are not always looking at. Someone arriving
// is worth knowing about even when your eyes are elsewhere, so it makes a
// sound -- one short, soft note pair, not an announcement.
//
// SYNTHESIZED with Web Audio rather than loaded from a file: no asset to ship,
// no request for the CSP to allow, and nothing to 404 after a deploy. The same
// approach the admin console uses for its alert, deliberately pitched
// differently so the two are never confused by anyone running both.
//
// Off is remembered per browser. Someone who finds it irritating turns it off
// once and it stays off, and the preference is local, so it says nothing about
// them to anybody else. ASCII only.
// ===========================================================================

const PREF_KEY = 'lumi.sound.join';

let joinSoundOn = true; // on by default; a single quiet note is not an imposition
try {
  joinSoundOn = localStorage.getItem(PREF_KEY) !== '0';
} catch {
  /* storage unavailable -- default to on for this page */
}

export function getJoinSoundOn() {
  return joinSoundOn;
}

export function setJoinSoundOn(on) {
  joinSoundOn = !!on;
  try {
    localStorage.setItem(PREF_KEY, on ? '1' : '0');
  } catch {
    /* not persisted, but active for this page */
  }
}

let ctx = null;

/** One AudioContext, created lazily. Browsers refuse to start one without a
 *  user gesture; joining a room IS a gesture, so by the time anything can
 *  sound, the page has had one. A suspended context is resumed on use. */
function audio() {
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

/**
 * Open the audio device NOW, while a real click is on the stack.
 *
 * Every tone here is played long after the gesture that led to it -- somebody
 * arrives, a message lands -- and a browser will not START an AudioContext
 * without a gesture. Creating it on first use therefore worked only by luck of
 * timing. Called from the Ready to Chat click, which is the last certain
 * gesture before a person sits waiting with the tab in the background, which
 * is precisely when the sound has to work.
 */
export function primeAudio() {
  audio();
}

function note(ac, freq, startAt, durationSec, peak) {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'sine'; // no harmonics to grate on the twentieth arrival
  osc.frequency.setValueAtTime(freq, startAt);
  // Ramp in and out. A tone that starts or stops at full amplitude clicks, and
  // a click in a video call sounds like a connection fault.
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSec);
  osc.connect(gain).connect(ac.destination);
  osc.start(startAt);
  osc.stop(startAt + durationSec + 0.02);
}

// A person arriving is a small, frequent event, so the tone is quieter and
// lower than the admin alert -- it should register at the edge of attention
// without pulling anyone out of the conversation they are already having.
export function playJoinTone() {
  if (!joinSoundOn) return;
  const ac = audio();
  if (!ac) return;
  try {
    const t = ac.currentTime + 0.01;
    note(ac, 523.25, t, 0.12, 0.09); // C5
    note(ac, 783.99, t + 0.09, 0.18, 0.075); // G5 -- rising, which reads as arrival
  } catch {
    /* a restricted or backgrounded tab can refuse; the tile still appears */
  }
}

// A notice -- a regular is around, you two are now regulars -- is rarer than an
// arrival and about a person you chose, so it may be a touch more present:
// two soft notes a fifth apart, falling rather than rising, so it reads as
// "something for you" and not as somebody walking in. Same off switch as the
// join tone: one preference, one place to turn the sounds off.
export function playNoticeTone() {
  if (!joinSoundOn) return;
  const ac = audio();
  if (!ac) return;
  try {
    const t = ac.currentTime + 0.01;
    note(ac, 659.25, t, 0.14, 0.1); // E5
    note(ac, 440.0, t + 0.12, 0.22, 0.085); // A4
  } catch {
    /* a restricted or backgrounded tab can refuse; the toast still shows */
  }
}

// A regular is calling. Louder than a notice -- it is the one sound here that
// asks for an answer -- but still two short notes rather than a bell: the same
// pair twice, rising, the way a phone's ring is a pattern and not a tone. The
// sheet that shows the call repeats this every few seconds while it is open
// (ui/call-sheets.js), and stops the moment anyone responds. Same off switch as
// the rest: somebody who turned the sounds off still sees the sheet and the
// sixty-second count, which is the call in full without the noise.
export function playRingTone() {
  if (!joinSoundOn) return;
  const ac = audio();
  if (!ac) return;
  try {
    const t = ac.currentTime + 0.01;
    note(ac, 587.33, t, 0.11, 0.13); // D5
    note(ac, 880.0, t + 0.1, 0.16, 0.12); // A5
    note(ac, 587.33, t + 0.36, 0.11, 0.13); // and again
    note(ac, 880.0, t + 0.46, 0.16, 0.12);
  } catch {
    /* a restricted or backgrounded tab can refuse; the sheet still shows */
  }
}

// A message that arrived while you were looking somewhere else (tab-attention.js).
// The quietest of the four on purpose: it fires for an ordinary line of chat,
// possibly while somebody is working in another window, so it has to be
// noticeable without being an event. One note, short, no interval to it -- a
// tap rather than a phrase. The three tones above all say "something happened
// here"; this one says "there is a message", and being plainer is what keeps
// them apart when two arrive together.
export function playMessageTone() {
  if (!joinSoundOn) return;
  const ac = audio();
  if (!ac) return;
  try {
    note(ac, 523.25, ac.currentTime + 0.01, 0.16, 0.07); // C5, soft
  } catch {
    /* a restricted or backgrounded tab can refuse; the title badge still shows */
  }
}

export default { primeAudio, playJoinTone, playNoticeTone, playRingTone, playMessageTone, getJoinSoundOn, setJoinSoundOn };
